import { OwnerMerchantOption } from '../../components/ui/merchantOptions';
import { createContext, useCallback, useState } from 'react';
import { AccessGroup, AccessLevel, BankOption, ResourceItem } from '../../models/interfaces';
import topApi from '../../components/api/ToPApi';
import { errorMessage } from '../../utils/access';

export interface GroupFilters {
    level?: AccessLevel;
    bankId?: number | null;
    merchantId?: number | null;
}

export interface GroupForm {
    id?: number;
    name: string;
    description?: string | null;
    level: AccessLevel;
    bankId?: number | null;
    merchantId?: number | null;
    resourceIds: number[];
}

interface GroupListResponse {
    groups: AccessGroup[];
    levels: AccessLevel[];
    banks: BankOption[];
    merchants: OwnerMerchantOption[];
}

type GroupsContextProps = {
    groups: AccessGroup[];
    levels: AccessLevel[];
    banks: BankOption[];
    merchants: OwnerMerchantOption[];
    isLoading: boolean;
    loadGroups: (filters: GroupFilters) => Promise<void>;
    loadResources: (level: AccessLevel, groupId?: number) => Promise<ResourceItem[]>;
    saveGroup: (group: GroupForm) => Promise<string>;
    deleteGroup: (id: number) => Promise<string>;
}

export const GroupsContext = createContext({} as GroupsContextProps);

// Grupos y permisos por nivel (plataforma, banco, comercio)
export const GroupsProvider = ({ children }: any) => {

    const [groups, setGroups] = useState<AccessGroup[]>([]);
    const [levels, setLevels] = useState<AccessLevel[]>([]);
    const [banks, setBanks] = useState<BankOption[]>([]);
    const [merchants, setMerchants] = useState<{ id: number; name: string; bankId?: number | null }[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadGroups = useCallback(async (filters: GroupFilters) => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.level) params.set('level', filters.level);
            if (filters.bankId) params.set('bankId', String(filters.bankId));
            if (filters.merchantId) params.set('merchantId', String(filters.merchantId));
            const resp = await topApi.get<GroupListResponse>(`/group/list?${params.toString()}`);
            setGroups(resp.data.groups ?? []);
            setLevels(resp.data.levels ?? []);
            setBanks(resp.data.banks ?? []);
            setMerchants(resp.data.merchants ?? []);
        } catch {
            setGroups([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Con groupId: lo que se puede otorgar en ese grupo (las plantillas de administrador admiten más permisos)
    const loadResources = useCallback(async (level: AccessLevel, groupId?: number) => {
        try {
            const resp = await topApi.get<{ resources: ResourceItem[] }>(`/group/resources/${level}`, { params: groupId ? { groupId } : undefined });
            return resp.data.resources ?? [];
        } catch {
            return [];
        }
    }, []);

    const saveGroup = async (group: GroupForm) => {
        try {
            if (group.id) await topApi.put(`/group/${group.id}`, { group });
            else await topApi.post('/group/', { group });
            return 'success';
        } catch (e) {
            return errorMessage(e);
        }
    }

    const deleteGroup = async (id: number) => {
        try {
            await topApi.delete(`/group/${id}`);
            setGroups(prev => prev.filter(group => group.id !== id));
            return 'success';
        } catch (e) {
            return errorMessage(e);
        }
    }

    return (
        <GroupsContext.Provider value={{ groups, levels, banks, merchants, isLoading, loadGroups, loadResources, saveGroup, deleteGroup }}>
            {children}
        </GroupsContext.Provider>
    );
}
